import React, { Fragment, useState, useEffect } from 'react'

import { classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  array, bool, func, number,
} from 'prop-types'

import NavigationButton from './NavigationButton'

// A centered loop with slidesPerView "auto" needs slidesPerView + ceil(slidesPerView / 2) slides, or
// Swiper warns and opens on the wrong slide. Slides are at least 30% wide, so up to 4 count as in view
// and 6 always suffice; smaller galleries repeat their images until they reach 6.
const LOOP_MIN_SLIDES = 6

function buildLoopSlides(slides) {
  const copies = slides.length > 1 ? Math.ceil(LOOP_MIN_SLIDES / slides.length) : 1

  return Array.from({ length: copies }, (_, copy) => (
    slides.map((url, index) => ({ url, index, copy }))
  )).flat()
}

let Swiper
let SwiperSlide
let Pagination
let Navigation
let FreeMode
let Keyboard

function Carousel({
  slides,
  onActiveIndexChange,
  highlightActive = false,
  activeIndex,
  totalCount,
}) {
  const [isCoreLoaded, setIsCoreLoaded] = useState(false)
  const [isModuleLoaded, setIsModuleLoaded] = useState(false)
  const loopSlides = buildLoopSlides(slides)

  const handleActiveIndexChange = ({ realIndex }) => {
    onActiveIndexChange({ realIndex: realIndex % slides.length })
  }

  function importCoreModules() {
    import('swiper/react').then((module) => {
      Swiper = module.Swiper
      SwiperSlide = module.SwiperSlide
      setIsCoreLoaded(true)
    })
  }

  function importAuxilaryModules() {
    import('swiper/modules').then((module) => {
      Pagination = module.Pagination
      Navigation = module.Navigation
      FreeMode = module.FreeMode
      Keyboard = module.Keyboard
      setIsModuleLoaded(true)
    })
  }

  useEffect(() => {
    importCoreModules()
    importAuxilaryModules()
  }, [])

  if (isCoreLoaded && isModuleLoaded) {
    return (
      <Fragment>
        <Swiper
          modules={[Navigation, Pagination, FreeMode, Keyboard]}
          className={classnames(
            'cgt-carousel-new',
            slides.length === 1 && 'cgt-carousel-new--single-slide'
          )}
          slidesPerView="auto"
          slidesPerGroup={1}
          lazyPreloadPrevNext={1}
          lazyPreloaderClass="cgt-carousel-new__image-loader"
          pagination={{
            clickable: false,
            type: 'custom',
          }}
          initialSlide={activeIndex}
          spaceBetween={0}
          onActiveIndexChange={handleActiveIndexChange}
          centeredSlides
          loop={slides.length > 1}
          slideToClickedSlide
          direction="horizontal"
          navigation={{
            nextEl: '.cgt-carousel-new__navigation-button--next',
            prevEl: '.cgt-carousel-new__navigation-button--prev',
          }}
          keyboard={{
            enabled: true,
            onlyInViewport: true,
          }}
        >
          {loopSlides.map(({ url, index, copy }) => (
            <SwiperSlide key={`cgt-slide-${copy}-${index}`}>
              {({ isActive }) => {
                const temp = `cgt-carousel-new__image
                  ${isActive ? 'cgt-carousel-new__image--active' : ''}
                  ${highlightActive ? 'cgt-carousel-new__image--highlight' : ''}`

                return (
                  <Fragment>
                    <img
                      alt=""
                      className={temp}
                      src={url}
                      loading={copy === 0 && index === 0 ? 'eager' : 'lazy'}
                    />
                    <div className="cgt-carousel-new__image-loader cgt-spinner" />
                  </Fragment>
                )
              }}
            </SwiperSlide>
          ))}
        </Swiper>
        {totalCount > 1 && (
          <Fragment>
            <NavigationButton
              icon="ArrowLeft"
              customClass="cgt-carousel-new__navigation-button--prev"
            />
            <NavigationButton
              icon="ArrowRight"
              customClass="cgt-carousel-new__navigation-button--next"
            />
          </Fragment>
        )}
      </Fragment>
    )
  }

  return <p>loading...</p>
}

Carousel.propTypes = {
  slides: array.isRequired,
  onActiveIndexChange: func.isRequired,
  highlightActive: bool,
  activeIndex: number.isRequired,
  totalCount: number.isRequired,
}

export default Carousel
